import React, { useEffect, useState, forwardRef } from "react";
import {
    Container,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme,
    Box,
    
    TableContainer,
    Divider
} from "@mui/material";
import {
    CartesianGrid,
    Legend,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
} from "recharts";

const COLORS = ["#1976d2", "#2e7d32", "#ed6c02", "#e91e63", "#00bcd4"];
const safeRound = (num) => parseFloat(num.toFixed(2)); // Utility to fix float precision

// Helper Functions (assuming external/available)
const getOrderTypeName = (typeCode) => {
    switch (typeCode) {
        case 0: return 'In-store'; case 1: return 'Pickup'; case 2: return 'Kiosk'; case 3: return 'Online'; default: return 'Other';
    }
};

export const SalesReportPage = forwardRef(({ reportRange, rawData }, ref) => {
    const theme = useTheme();
    const [chartData, setChartData] = useState([]);
    const [pieData, setPieData] = useState([]);
    const [orderSummary, setOrderSummary] = useState({});
    const [paymentSummary, setPaymentSummary] = useState({});
    const [cashDropTotal, setCashDropTotal] = useState(0);
    const [paidOutTotal, setPaidOutTotal] = useState(0);
    const [salesSummary, setSalesSummary] = useState({});

    useEffect(() => {
        // Find the correct arrays to process
        const dailyDataArray = rawData?.dailyData || [];
        const rawOrdersArray = rawData?.orders || [];

        if (!dailyDataArray || dailyDataArray.length === 0) {
            console.log("Did not find valid array data to process.");
            setChartData([]);
            setPieData([]);
            setOrderSummary({});
            setPaymentSummary({});
            setCashDropTotal(0);
            setPaidOutTotal(0);
            setSalesSummary({});
            return;
        }

        const hourlySales = {};
        const paymentMap = { Cash: 0, "Credit Card": 0, "Gift Card": 0, Online: 0, Other: 0 };
        let totalTips = 0;
        let completed = 0;
        let voided = 0;
        const orderTypeMap = { "In-store": 0, Pickup: 0, Kiosk: 0, Online: 0, Other: 0 };

        let aggregatedCashDrops = 0;
        let aggregatedPaidOuts = 0;
        let aggregatedTotalSales = 0;

        // --- 1. Aggregate Daily Summaries (KPIs, Cash Flow) ---
        // This loop calculates the overall totals from the backend summaries.
        dailyDataArray.forEach(dayData => {
            aggregatedTotalSales += dayData.totalSales ?? 0;
            aggregatedCashDrops += (dayData.cashDrops || []).reduce((acc, drop) => acc + (drop.amount ?? 0), 0);
            aggregatedPaidOuts += (dayData.paidOuts || []).reduce((acc, payout) => acc + (payout.amount ?? 0), 0);
            // We use the totalTips from the loop below (from the detailed orders) for consistency.
        });

        // --- 2. Process Individual Orders (Breakdowns, Charts) ---
        // Iterate through all orders (completed, pending, or cancelled)
        for (let order of rawOrdersArray) {
            
            if (order.oStatus === 5) {
                // Count cancelled/voided orders
                voided++;
            }
            
            // Only process COMPLETED orders (oStatus === 2) for sales metrics
            if (order.oStatus === 2) {
                
                // Sales Trend/Subtotal Calculations:
                const orderDate = new Date(order.startTime);
                const hour = orderDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                hourlySales[hour] = (hourlySales[hour] || 0) + (order.total ?? 0);
                
                totalTips += order.Tip ?? 0;
                completed++;
                
                // Loop payments for breakdown
                for (let payment of order.payment || []) {
                    const amount = payment.amount ?? 0;
                    // const tip = payment.tip ?? 0; // Use payment tip for breakdown accuracy

                    switch (payment.payType) {
                        case 0: paymentMap["Cash"] += amount; break;
                        case 1: paymentMap["Gift Card"] += amount; break;
                        case 2: paymentMap["Online"] += amount; break;
                        case 3: paymentMap["Credit Card"] += amount; break;
                        default: paymentMap["Other"] += amount;
                    }
                }

                // Map order type
                const typeName = getOrderTypeName(order.type);
                orderTypeMap[typeName] = (orderTypeMap[typeName] || 0) + 1;
            }
        }
        // --- END OF ORDER PROCESSING ---

        // Finalize hourly data for chart
        const finalChartData = Object.entries(hourlySales).map(([date, total]) => ({ date, total: safeRound(total) }));
        
        // Finalize payment breakdown by rounding totals
        const finalPaymentMap = Object.entries(paymentMap).reduce((acc, [type, total]) => {
            acc[type] = safeRound(total);
            return acc;
        }, {});
        
        // Finalize totals for over/short calculation
        const cashSales = finalPaymentMap["Cash"] || 0;
        const openingCash = dailyDataArray[0]?.openingCash ?? 0; // Get opening cash from the *first* day
        const tillCash = dailyDataArray[dailyDataArray.length - 1]?.tillCash ?? 0; // Get till cash from the *last* day

        const expectedCash = openingCash + cashSales - aggregatedCashDrops - aggregatedPaidOuts;
        const difference = tillCash - expectedCash;
        
        // NOTE: Since the backend provides totalSales, we calculate estimated tax and subtotal based on that.
        const defaultTaxRate = 0.05; // Assuming a fallback 5% sales tax rate (0.05)
        const estimatedTax = aggregatedTotalSales * defaultTaxRate; // Simple estimate
        const finalSubTotal = aggregatedTotalSales - estimatedTax;


        // Set all final states
        setChartData(finalChartData);
        setPieData(Object.entries(finalPaymentMap).map(([name, value]) => ({ name, value })));
        setOrderSummary({ ...orderTypeMap, Completed: completed, Void: voided });
        setPaymentSummary(finalPaymentMap);
        setCashDropTotal(safeRound(aggregatedCashDrops));
        setPaidOutTotal(safeRound(aggregatedPaidOuts));

        setSalesSummary({
            subTotal: safeRound(finalSubTotal), 
            tax: safeRound(estimatedTax), 
            total: safeRound(aggregatedTotalSales),
            tips: safeRound(totalTips),
            openingCash: safeRound(openingCash),
            cashCount: safeRound(tillCash),
            overShort: safeRound(difference)
        });

    }, [reportRange, rawData]);


    return (
        <Container sx={{ mt: 4, mb: 4 }} ref={ref}>
            <Paper elevation={3} sx={{ p: 3, mb: 3, backgroundColor: "#f5faff" }}>
                <Typography variant="h6" color="secondary" gutterBottom>
                    Sales Overview
                </Typography>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                         <Box sx={{ height: 300, width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis label={{ value: 'Sales ($)', angle: -90, position: 'insideLeft' }} />
                                    <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                                    <Legend />
                                    <Line type="monotone" dataKey="total" stroke={theme.palette.primary.main} />
                                </LineChart>
                            </ResponsiveContainer>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                         <Box sx={{ height: 300, width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={80}
                                        label
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, backgroundColor: "#fff8e1" }}>
                        <Typography variant="h6" color="warning.main" gutterBottom>
                            Order Summary
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Order Type</TableCell>
                                        <TableCell align="right">Count</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(orderSummary).map(([type, count]) => (
                                        <TableRow key={type}>
                                            <TableCell>{type}</TableCell>
                                            <TableCell align="right">{count}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, backgroundColor: "#e1f5fe" }}>
                        <Typography variant="h6" color="info.main" gutterBottom>
                            Payment Summary
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Payment Type</TableCell>
                                        <TableCell align="right">Amount ($)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.entries(paymentSummary).map(([type, amount]) => (
                                        <TableRow key={type}>
                                            <TableCell>{type}</TableCell>
                                            {/* Use optional chaining for safe number formatting */}
                                            <TableCell align="right">${amount?.toFixed(2) ?? '0.00'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Divider sx={{ my: 1 }}/>

                        <Typography variant="subtitle2">Total Cash Drops : ${cashDropTotal?.toFixed(2) ?? '0.00'}</Typography>

                        <Typography variant="subtitle2" sx={{ mt: 1 }}>
                            Total Paid Outs : ${paidOutTotal?.toFixed(2) ?? '0.00'}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            <Paper elevation={2} sx={{ p: 3, mt: 3, backgroundColor: "#f3e5f5" }}>
                <Typography variant="h6" color="secondary" gutterBottom>
                    Sales Summary
                </Typography>
                <TableContainer>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell>Sub Total</TableCell>
                                <TableCell align="right">${salesSummary.subTotal?.toFixed(2) ?? '0.00'}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Tax</TableCell>
                                <TableCell align="right">${salesSummary.tax?.toFixed(2) ?? '0.00'}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Total Sale</TableCell>
                                <TableCell align="right">${salesSummary.total?.toFixed(2) ?? '0.00'}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Tips</TableCell>
                                <TableCell align="right">${salesSummary.tips?.toFixed(2) ?? '0.00'}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Opening Cash</TableCell>
                                <TableCell align="right">${salesSummary.openingCash?.toFixed(2) ?? '0.00'}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Cash Count</TableCell>
                                <TableCell align="right">${salesSummary.cashCount?.toFixed(2) ?? '0.00'}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Over/Short</TableCell>
                                <TableCell align="right" sx={{ color: "error.main" }}>
                                    ${salesSummary.overShort ?? '0.00'}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Container>
    );
});